const { DEFAULT_RECIPE, calculateRecipe, formatAmount, shiftRecipe } = await import((typeof window !== 'undefined' && window.HG_ASSETS?.['brew-model.js']) || new URL('./hg12-brew-model.js', import.meta.url).href);

const advice = {
  sour: {
    title: 'Try a slightly finer grind.',
    action: 'For your next brew, keep the coffee dose, water amount and pouring method the same.',
    caution: 'Try a small change and taste again. Some coffees have a pleasant fruit acidity; a sharper taste doesn’t always mean something went wrong. You can return to your previous setting.'
  },
  bitter: {
    title: 'Try a slightly coarser grind.',
    action: 'For your next brew, keep the rest of your recipe the same.',
    caution: 'Roast character and uneven brewing can also contribute to bitterness. A small grind change is a place to start; it may not remove every bitter note.'
  },
  intensity: {
    title: 'Keep the flavor. Explore the intensity.',
    action: 'With the same coffee dose and grind, try More intensity for a little less brewing water, or Gentler for a little more.',
    caution: 'Use this when the flavor feels right. Changing the water can also affect extraction, so taste the next brew before adjusting again.'
  }
};

export function initBrew(root) {
  if (!root || root.dataset.bReady === 'true') return;
  const get = name => root.querySelector(`[data-b-${name}]`);
  const coffee = get('coffee');
  const ratio = get('ratio');
  const controls = get('controls');
  const recipe = get('recipe');
  const water = get('water');
  const coffeeResult = get('coffee-result');
  const ratioResult = get('ratio-result');
  const status = get('status');
  const invalid = get('invalid');
  const stronger = get('stronger');
  const gentler = get('gentler');
  const reset = get('reset');
  const printButton = get('print');
  const printAction = get('print-action');
  const coffeeError = get('coffee-error');
  const ratioError = get('ratio-error');
  const staticNote = get('static-note');
  const tips = get('tips');
  const tipPanel = get('advice');
  const tipTitle = get('advice-title');
  const tipAction = get('advice-action');
  const tipCaution = get('advice-caution');
  if (![coffee, ratio, controls, recipe, water, coffeeResult, ratioResult, status,
    invalid, stronger, gentler, reset, printButton, printAction, coffeeError, ratioError, staticNote, tips,
    tipPanel, tipTitle, tipAction, tipCaution].every(Boolean)) return;

  function fieldError(input, message, node) {
    node.textContent = message || '';
    node.hidden = !message;
    if (message) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
  }

  function render(announce = false) {
    const state = calculateRecipe(coffee.value, ratio.value);
    fieldError(coffee, state.errors.coffee, coffeeError);
    fieldError(ratio, state.errors.ratio, ratioError);
    recipe.hidden = !state.valid;
    invalid.hidden = state.valid;
    stronger.disabled = !state.valid || state.ratio <= 15;
    gentler.disabled = !state.valid || state.ratio >= 18;
    printButton.disabled = !state.valid;
    if (!state.valid) {
      water.value = '';
      coffeeResult.textContent = '';
      ratioResult.textContent = '';
      status.textContent = announce ? `Let’s check those amounts. ${Object.values(state.errors).join(' ')}` : '';
      return state;
    }
    coffeeResult.textContent = `${formatAmount(state.coffee)} g`;
    ratioResult.textContent = `1:${formatAmount(state.ratio)}`;
    water.value = `${formatAmount(state.water)} g`;
    status.textContent = announce
      ? `${formatAmount(state.coffee)} grams of coffee at a 1 to ${formatAmount(state.ratio)} ratio: add ${formatAmount(state.water)} grams of water for brewing.`
      : '';
    return state;
  }

  coffee.addEventListener('input', () => render());
  coffee.addEventListener('change', () => render(true));
  ratio.addEventListener('change', () => render(true));

  function adjust(direction) {
    const next = shiftRecipe(coffee.value, ratio.value, direction);
    if (!next) return;
    ratio.value = String(next.ratio);
    render(true);
  }
  stronger.addEventListener('click', () => adjust('stronger'));
  gentler.addEventListener('click', () => adjust('gentler'));

  reset.addEventListener('click', () => {
    coffee.value = String(DEFAULT_RECIPE.coffee);
    ratio.value = String(DEFAULT_RECIPE.ratio);
    render(true);
  });

  printButton.addEventListener('click', () => {
    // Revalidate here as well, so a programmatic click cannot print stale values.
    const state = render();
    if (!state.valid) return;
    const printWindow = root.ownerDocument?.defaultView;
    if (typeof printWindow?.print === 'function') printWindow.print();
  });

  const tipButtons = [...root.querySelectorAll('[data-b-tip]')];
  tipButtons.forEach(button => {
    button.addEventListener('click', () => {
      const selected = advice[button.dataset.bTip];
      if (!selected) return;
      tipButtons.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
      tipTitle.textContent = selected.title;
      tipAction.textContent = selected.action;
      tipCaution.textContent = selected.caution;
      tipPanel.hidden = false;
      status.textContent = `${selected.title} ${selected.action} ${selected.caution}`;
    });
  });

  render();
  controls.hidden = false;
  printAction.hidden = false;
  tips.hidden = false;
  staticNote.hidden = true;
  root.dataset.bReady = 'true';
}

if (typeof document !== 'undefined') {
  const init = () => document.querySelectorAll('[data-brew]').forEach(initBrew);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
}
